import React, { useEffect, useState, useMemo } from 'react';
import { Users, UserCheck, DollarSign, Activity, LucideIcon, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Card } from '../ui/card';
import { usersApi, DashboardStats } from '../../api/users';

interface KPICardsProps {
  isDark: boolean;
}

interface KPICardData {
  id: number;
  title: string;
  value: number;
  icon: LucideIcon;
  iconColor: string;
  isCurrency?: boolean;
}

export default function KPICards({ isDark }: KPICardsProps) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await usersApi.getDashboardStats();
        setStats(data);
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load dashboard statistics');
        console.error('Error fetching dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatValue = (value: number, isCurrency = false): string => {
    if (isCurrency) {
      return new Intl.NumberFormat('en-US', {
        style: 'decimal',
        currency: 'UZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    }
    return new Intl.NumberFormat('en-US').format(value);
  };

  const kpiData: KPICardData[] = useMemo(() => {
    if (!stats) return [];
    
    return [
      {
        id: 1,
        title: 'Total Users',
        value: stats.totalUsers,
        icon: Users,
        iconColor: 'from-blue-500 to-blue-600',
      },
      {
        id: 2,
        title: 'Subscribed Users',
        value: stats.subscribedUsers,
        icon: UserCheck,
        iconColor: 'from-emerald-500 to-emerald-600',
      },
      {
        id: 3,
        title: 'Total Revenue',
        value: stats.totalRevenue,
        icon: DollarSign,
        iconColor: 'from-purple-500 to-purple-600',
        isCurrency: true,
      },
      {
        id: 4,
        title: 'Active Sessions',
        value: stats.activeUsersByPeriod[0]?.current || 0,
        icon: Activity,
        iconColor: 'from-orange-500 to-orange-600',
      },
    ];
  }, [stats]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Card
            key={i}
            className={cn(
              'p-5 rounded-2xl border-0',
              isDark ? 'bg-[#1E293B]' : 'bg-white'
            )}
          >
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-[#0EA5A4]" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <Card
          className={cn(
            'p-5 rounded-2xl border-0 col-span-full',
            isDark ? 'bg-[#1E293B]' : 'bg-white'
          )}
        >
          <p className={cn(
            'text-sm',
            isDark ? 'text-red-400' : 'text-red-600'
          )}>
            Error: {error}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
      {kpiData.map((kpi) => {
        const Icon = kpi.icon;

        return (
          <Card
            key={kpi.id}
            className={cn(
              'p-5 rounded-2xl border-0 transition-all duration-300 hover:shadow-xl hover:-translate-y-1',
              isDark
                ? 'bg-[#1E293B] shadow-lg shadow-black/10'
                : 'bg-white shadow-lg shadow-slate-200/50'
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p
                  className={cn(
                    'text-sm font-medium',
                    isDark ? 'text-slate-400' : 'text-slate-500'
                  )}
                >
                  {kpi.title}
                </p>
                <p
                  className={cn(
                    'text-2xl lg:text-3xl font-bold mt-2 tracking-tight',
                    isDark ? 'text-white' : 'text-[#0F172A]'
                  )}
                >
                  {formatValue(kpi.value, kpi.isCurrency)}
                </p>
              </div>
              <div
                className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center',
                  `bg-gradient-to-br from-[#0EA5A4] to-[#0EA5A4]/70 shadow-lg shadow-[#0EA5A4]/20`
                )}
              >
                <Icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

